import { Link } from 'react-router';
import { Skeleton } from '@awj/ui';

import { Icon, type IconName } from './Icon.tsx';

/**
 * How loudly the figure itself asks for attention. `plain` is a reading; `quiet` is a queue with
 * nothing in it; `warning` and `danger` are queues waiting on someone, in rising order of how much
 * that matters — and the only ones whose figure takes a colour.
 */
type Tone = 'plain' | 'quiet' | 'warning' | 'danger';

/** The colour of the figure's glyph tile — see the dashboard's palette in admin.css. */
export type StatColour = 'violet' | 'blue' | 'green' | 'orange' | 'red' | 'sky';

/**
 * One figure, and where to act on it.
 *
 * The figure's glyph in a tile of its colour at the reading start, and beside it the label over the
 * value. A narrow card stacks the tile over the text instead, so the value always has the card's
 * width (admin.css, the dashboard). `wave` draws a soft rise of the card's colour along its foot —
 * the trade cards wear it; the queues, sharing one panel, do not.
 *
 * The label always says what the figure is, so a reader who cannot tell the colours apart loses
 * nothing, and the glyph is hidden from assistive technology: the link's name is the label and the
 * figure, as before.
 *
 * A card with a `to` becomes a link to the queue it counts, so nobody reads "3 pending reviews"
 * and then hunts for the reviews page.
 */
export function StatCard({
  label,
  value,
  icon,
  colour = 'violet',
  wave = false,
  loading = false,
  tone = 'plain',
  to,
}: {
  label: string;
  /** Formatted by the caller, which is where the locale lives. Null while loading or failed. */
  value: string | null;
  icon: IconName;
  colour?: StatColour;
  wave?: boolean;
  loading?: boolean;
  tone?: Tone;
  to?: string;
}) {
  const body = (
    <>
      <span className="stat-card__body">
        <span className="stat-card__icon" aria-hidden="true">
          <Icon name={icon} size={19} />
        </span>
        <span className="stat-card__text">
          <span className="stat-card__label">{label}</span>
          {loading ? (
            <span className="stat-card__loading">
              <Skeleton height="20px" width="4.5rem" />
            </span>
          ) : (
            <span className="stat-card__metric">
              {/* Isolated: an amount or a count beside a label in the other direction. */}
              <bdi className="stat-card__value">{value ?? '—'}</bdi>
            </span>
          )}
        </span>
      </span>

      {wave ? (
        <svg className="stat-card__wave" viewBox="0 0 240 48" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 30C30 18 52 16 80 24s52 22 84 14 52-26 76-20V48H0z" fillOpacity="0.07" />
          <path d="M0 36C34 28 60 26 92 32s58 14 88 8 44-18 60-16V48H0z" fillOpacity="0.11" />
        </svg>
      ) : null}
    </>
  );

  const className = `stat-card stat-card--${tone}`;

  return to ? (
    <Link className={`${className} stat-card--interactive`} data-colour={colour} to={to}>
      {body}
    </Link>
  ) : (
    <div className={className} data-colour={colour}>
      {body}
    </div>
  );
}
